/**
 * Filter section for the ASN list tab.
 *
 * Grouped compact layout matching SO Lines / SO Headers filter panels.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { X, Calendar, Search, Filter } from 'lucide-react';
import SearchableSelect from '../../ui/SearchableSelect';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import {
  getDeliveryLineCustomerColumnLabel,
  getDeliveryLineCustomerGroupLabel,
} from '../../../lib/portalLabels';

interface FilterOption {
  value: string;
  label: string;
}

interface ASNFilterSectionProps {
  filters: {
    asnNumber: string;
    customer: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
    status: string;
    courier: string;
    courierDeliveryStatus: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    customerGroup: string;
    commodityGroup: string;
    commodityType: string;
    strategic: string;
  };
  textInputs: {
    asnNumber: string;
    customer: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
  };
  siteOptions: FilterOption[];
  customerOptions: FilterOption[];
  customerGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  courierOptions: FilterOption[];
  /** When true, limit ASN status choices to customer-visible states. */
  isCustomer?: boolean;
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  onHideFilters?: () => void;
}

export function ASNFilterSection({
  filters,
  textInputs,
  siteOptions,
  customerOptions,
  customerGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  courierOptions,
  isCustomer = false,
  onFilterChange,
  onClearFilters,
  onSearch,
  onHideFilters,
}: ASNFilterSectionProps) {
  const { isSupplierPortalMode } = useMenuSettings();
  const counterpartyLabel = getDeliveryLineCustomerColumnLabel();
  const counterpartyGroupLabel = getDeliveryLineCustomerGroupLabel();
  const siteFilterLabel = isSupplierPortalMode ? 'Site' : 'Company Site';

  const inputCls = 'w-full h-8 px-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const dateInputCls = 'w-full h-8 pl-7 pr-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const labelCls = 'text-xs font-medium mb-1 block text-foreground leading-tight';
  const sectionTitleCls = 'text-[11px] font-semibold uppercase tracking-wide text-foreground mb-1.5';
  const sectionCls = 'rounded-md border border-border/50 bg-muted/20 px-2.5 py-2';
  const compactGridCls = 'grid grid-cols-2 gap-x-2 gap-y-1.5';
  const selectCompactCls = '[&>button]:h-8 [&>button]:min-h-8 [&>button]:py-1 [&>button]:px-2 [&>button]:text-sm';

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-4 border-b border-border/60">
        <CardTitle className="text-sm font-semibold">Filter ASNs</CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-3 space-y-2">
        <div className={sectionCls}>
          <div className={sectionTitleCls}>Customer &amp; Site</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>{siteFilterLabel}</label>
              <SearchableSelect
                options={siteOptions}
                value={filters.site}
                onChange={(val) => onFilterChange('site', val)}
                placeholder={`All ${siteFilterLabel}s`}
                className={selectCompactCls}
              />
            </div>
            <div>
              <label className={labelCls}>{counterpartyGroupLabel}</label>
              <SearchableSelect
                options={customerGroupOptions}
                value={filters.customerGroup}
                onChange={(val) => onFilterChange('customerGroup', val)}
                placeholder={`All ${counterpartyGroupLabel}s`}
                className={selectCompactCls}
              />
            </div>
            <div>
              <label className={labelCls}>{counterpartyLabel}</label>
              <SearchableSelect
                options={customerOptions}
                value={filters.customer}
                onChange={(val) => onFilterChange('customer', val)}
                placeholder={`All ${counterpartyLabel}s`}
                className={selectCompactCls}
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-2">
          <div className={sectionCls}>
            <div className={sectionTitleCls}>Part &amp; Commodity</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>Commodity Group</label>
                <select
                  value={filters.commodityGroup}
                  onChange={(e) => onFilterChange('commodityGroup', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  {commodityGroupOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Commodity Type</label>
                <select
                  value={filters.commodityType}
                  onChange={(e) => onFilterChange('commodityType', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  {commodityTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={sectionCls}>
            <div className={sectionTitleCls}>Dates</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>Date From</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                    className={dateInputCls}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Date To</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => onFilterChange('dateTo', e.target.value)}
                    className={dateInputCls}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={sectionCls}>
          <div className={sectionTitleCls}>Order</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>SO Number</label>
              <input
                type="text"
                placeholder="SO Number..."
                value={textInputs.poNumber}
                onChange={(e) => onFilterChange('poNumber', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>SO Line Number</label>
              <input
                type="text"
                placeholder="Line number..."
                value={textInputs.poLineNumber}
                onChange={(e) => onFilterChange('poLineNumber', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>ASN Number</label>
              <input
                type="text"
                placeholder="ASN Number..."
                value={textInputs.asnNumber}
                onChange={(e) => onFilterChange('asnNumber', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className={sectionCls}>
          <div className={sectionTitleCls}>Status &amp; Logistics</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>ASN Status</label>
              <select
                value={filters.status}
                onChange={(e) => onFilterChange('status', e.target.value)}
                className={inputCls}
              >
                <option value="">All</option>
                {!isCustomer && <option value="created">Created</option>}
                {!isCustomer && <option value="submitted">Submitted</option>}
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
                {!isCustomer && <option value="cancelled">Cancelled</option>}
              </select>
            </div>
            <div>
              <label className={labelCls}>Courier</label>
              <select
                value={filters.courier}
                onChange={(e) => onFilterChange('courier', e.target.value)}
                className={inputCls}
              >
                <option value="">All</option>
                {courierOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Tracking Number</label>
              <input
                type="text"
                placeholder="Tracking number..."
                value={textInputs.trackingNumber}
                onChange={(e) => onFilterChange('trackingNumber', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Courier Delivery Status</label>
              <select
                value={filters.courierDeliveryStatus}
                onChange={(e) => onFilterChange('courierDeliveryStatus', e.target.value)}
                className={inputCls}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="in_transit">In Transit</option>
                <option value="out_for_delivery">Out for Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
          <Button type="button" size="sm" className="px-5" onClick={onSearch}>
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onClearFilters} className="px-5">
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button type="button" variant="outline" size="sm" onClick={onHideFilters}>
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Hide Filters
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
